import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  computed,
  effect,
  untracked,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MarkdownModule, provideMarkdown } from 'ngx-markdown';
import { environment } from '../../../../environments/environment';
import { IconComponentV2 } from '../../../shared/components/icon-component/icon.component';
import { PaginatedTableComponent, TableHeader, TableActionOptions } from '../../../shared/components/tables/paginated-table/paginated-table.component';
import { PaginationComponent } from '../../../shared/components/pagination/pagination.component';

import { ProductConfigService } from '../../../core/services/product-config.service';
import { CallTranscriptionComponent } from '../../private/call-history/call-transcription/call-transcription.component';
import jsPDF from 'jspdf';
import {
  SelectionComponent,
} from '../../../shared/components/ui/selection/selection.component';
import {
  DropdownComponent,
  DropdownOption,
} from '../../../shared/components/ui/dropdown/dropdown.component';
import { ApiService } from '../../../core/services/api.service';
import { LiveMonitoringComponent } from './live-monitoring/live-monitoring.component';
import { ToggleComponent } from '../../../shared/components/ui/toggle/toggle.component';
import {
  extractApiArray,
  mapOngoingCallListItem,
  dedupeOngoingCalls,
  OngoingCallListItem,
  ONGOING_CALLS_REFRESH_MS,
} from './crm-panel.utils';
import { Subscription, interval } from 'rxjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const LA_TIMEZONE = 'America/Los_Angeles';

type CrmLeadStatusFilter = 'ALL' | 'lead_added' | 'sales_pitch_success' | 'sales_pitch_failed';

export interface FormattedTranscriptLine {
  time: string;
  timeSeconds: number;
  speaker: string;
  role: 'AI' | 'AGENT' | 'CUSTOMER' | 'SYSTEM';
  text: string;
  sender: string;
  content: string;
  formattedTime: string;
}

export interface AuditExcerpt {
  label: string;
  text: string;
  time?: string;
  role: 'AI' | 'AGENT' | 'CUSTOMER' | 'SYSTEM';
}

export interface AuditCategory {
  id: number;
  title: string;
  score: number;
  justification: string;
  analysis: string;
  analysisSub: string;
  wentWell: string[];
  opportunities: string[];
  excerpts: AuditExcerpt[];
}

export interface CrmInsightSection {
  title: string;
  content: string;
}

export interface CallVerdictView {
  label: string;
  reason: string;
  category: string;
  confidence: number | null;
}

export interface CostingServicePrice {
  serviceName: string;
  price: string;
  source: string;
  confidence: string;
  pricePeriod: string;
}

export interface CostingView {
  totalCost: string;
  displayCost: string;
  currency: string;
  provider: string;
  model: string;
  tokens: number | null;
  rows: { label: string; value: string }[];
  servicesWithPrices: CostingServicePrice[];
}

export interface AudioTrack {
  url: string;
  duration: number;
}

export interface RecordingView {
  audioUrl: string;
  tracks: AudioTrack[];
  referer: string;
  duration: number;
  productName: string;
  platform: string;
}

export interface SummaryLogView {
  managementSummary: string;
  highlights: string[];
  majorIssues: string[];
  callFlow: { title: string; summary: string }[];
  discussionPoints: string[];
  prospectSnapshot: string[];
}

type CrmDetailSection = 'recording' | 'summary' | 'insights' | 'audit';

export interface ConversationViewModel {
  conversationPid: number;
  conversationStartTime: string | null;
  conversationEndTime: string | null;
  agentName: string;
  agentEmail: string;
  callType: string;
  platform: string;
  overviewSummary: string;
  finalSummary: {
    weightedScore: number;
    highlights: string[];
    performance: string;
    coaching: string[];
    commentary: string;
    majorIssues: string[];
  };
  callOverview: {
    contactType: string;
    summary: string;
    tone: string;
    callStatus: string;
  };
  bizIntel: {
    interestSignal: boolean;
    demoBooked: boolean;
    followUpRequested: boolean;
    industry: string;
    sentiment: string;
    callOutcome: string;
    shortSummary: string;
  };
  topicAnalysis: {
    relevance: string;
    keyPoints: string[];
  };
  auditCategories: AuditCategory[];
  formattedTranscript: FormattedTranscriptLine[];
  verdict: CallVerdictView;
  cheatSheet: CrmInsightSection[];
  salesPitch: CrmInsightSection[];
  costing: CostingView | null;
  recording: RecordingView | null;
  hasAudit: boolean;
  hasSummary: boolean;
  summaryLogView: SummaryLogView | null;
}

@Component({
  selector: 'app-crm-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MarkdownModule,
    IconComponentV2,
    PaginatedTableComponent,
    PaginationComponent,
    // ThemeToggleComponent,
    SelectionComponent,
    DropdownComponent,
    CallTranscriptionComponent,
    LiveMonitoringComponent,
    ToggleComponent,
  ],
  providers: [provideMarkdown()],
  templateUrl: './crm-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host ::ng-deep .markdown-content {
        p {
          margin-bottom: 0.5em;
          line-height: 1.5;
        }
        p:last-child {
          margin-bottom: 0;
        }
        strong {
          font-weight: 700;
        }
        ul {
          list-style: disc;
          margin: 0.5em 0;
          padding-left: 1.5em;
        }
      }
      :host ::ng-deep .insight-markdown {
        color: inherit;
      }
      :host ::ng-deep .insight-markdown p,
      :host ::ng-deep .insight-markdown li {
        color: inherit;
        line-height: 1.65;
      }
      :host ::ng-deep .insight-markdown ul,
      :host ::ng-deep .insight-markdown ol {
        margin: 0.35rem 0;
        padding-left: 1.1rem;
      }
      :host ::ng-deep .insight-markdown strong {
        color: inherit;
      }
      .crm-tab-panel {
        animation-duration: 420ms;
        animation-timing-function: cubic-bezier(0.2, 0.8, 0.2, 1);
        animation-fill-mode: both;
        backface-visibility: hidden;
        transform: translateZ(0);
        transition:
          opacity 420ms cubic-bezier(0.2, 0.8, 0.2, 1),
          transform 420ms cubic-bezier(0.2, 0.8, 0.2, 1);
        will-change: opacity, transform;
      }
      .crm-tab-panel--from-right {
        animation-name: crmTabFromRight;
      }
      .crm-tab-panel--from-left {
        animation-name: crmTabFromLeft;
      }
      @keyframes crmTabFromRight {
        from {
          opacity: 0.98;
          transform: translate3d(28px, 0, 0);
        }
        70% {
          opacity: 1;
        }
        to {
          opacity: 1;
          transform: translate3d(0, 0, 0);
        }
      }
      @keyframes crmTabFromLeft {
        from {
          opacity: 0.98;
          transform: translate3d(-28px, 0, 0);
        }
        70% {
          opacity: 1;
        }
        to {
          opacity: 1;
          transform: translate3d(0, 0, 0);
        }
      }
    `,
  ],
})
export class CrmPanelComponent implements OnInit, OnDestroy {
  private apiService = inject(ApiService);
  private productConfigService = inject(ProductConfigService);

  activeCrmView = signal<'CALL_HISTORY' | 'LIVE_MONITORING'>('CALL_HISTORY');
  isTest = signal<boolean>(false);
  pendingIsTest = signal<boolean>(false);
  ongoingCallsCount = signal(0);

  private ongoingCountPollSub: Subscription | null = null;

  isLoading = signal(true);
  error = signal<string | null>(null);
  leads = signal<any[]>([]);
  searchQuery = signal('');
  pendingSearchQuery = signal('');
  readonly minFilterDate = '2025-01-01';
  readonly maxFilterDate = dayjs.utc().tz(LA_TIMEZONE).format('YYYY-MM-DD');
  pendingStartDate = signal<string | null>(this.minFilterDate);
  pendingEndDate = signal<string | null>(this.maxFilterDate);
  startDateFilter = signal<string | null>(this.minFilterDate);
  endDateFilter = signal<string | null>(this.maxFilterDate);
  pendingStatusFilter = signal<CrmLeadStatusFilter>('ALL');
  statusFilter = signal<CrmLeadStatusFilter>('ALL');
  readonly statusFilterOptions: DropdownOption[] = [
    { value: 'ALL', label: 'All' },
    { value: 'lead_added', label: 'Lead Added' },
    { value: 'sales_pitch_success', label: 'Sales Pitch Success' },
    { value: 'sales_pitch_failed', label: 'Sales Pitch Failed' },
  ];
  endDateMinFilter = computed(() => this.pendingStartDate() || this.minFilterDate);

  private lastSidebarProductPid: number | null | undefined = undefined;

  readonly sidebarProductPid = computed(() => {
    const id =
      this.apiService.selectedProductId() ||
      sessionStorage.getItem('selected_product_id');
    const products = this.apiService.products();
    const product = products.find((p) => p.id === id);
    let pid = product?.pid;

    if (pid == null && id) {
      try {
        const stored = sessionStorage.getItem('dynamic_routes');
        if (stored) {
          const dynamicRoutes = JSON.parse(stored);
          const matched = dynamicRoutes.find((p: any) => p.product?.id === id);
          if (matched && matched.product?.pid !== undefined) {
            pid = matched.product.pid;
          }
        }
      } catch (e) {
        console.error('Error resolving productPid from dynamic_routes in crm-panel:', e);
      }
    }

    return pid != null && String(pid).trim() ? Number(pid) : null;
  });

  selectedLeadIndex = signal<number | null>(null);
  selectedConvIndex = signal<number | null>(null);
  conversationPage = signal(1);
  readonly conversationPageSize = 10;
  isFullTranscriptModalOpen = signal(false);
  isCheatSheetModalOpen = signal(false);
  activeDetailSection = signal<CrmDetailSection>('recording');
  tabAnimationDirection = signal<'left' | 'right'>('right');
  private readonly detailSectionOrder: CrmDetailSection[] = [
    'recording',
    'summary',
    'insights',
    'audit',
  ];

  activePlayingCall = signal<any | null>(null);
  isPlaying = signal(false);
  isMuted = signal(false);
  currentTime = signal(0);
  audioDuration = signal(0);
  activeTranscriptIndex = signal(-1);
  private audio = new Audio();
  private isChangingAudioSource = false;

  // Pagination & Table (server-side via all-leads-details API)
  currentPage = signal(1);
  pageSize = signal(10);
  totalItems = signal(0);
  serverSidePagination = signal(true);

  tableHeaders: TableHeader[] = [
    { label: 'Business Name', key: 'businessName' },
    { label: 'Product', key: 'productName', cellType: 'badge' },
    { label: 'Platform', key: 'platformType' },
    { label: 'Status', key: 'statusLabel', cellType: 'badge' },
    { label: 'Created At', key: 'customerCreatedAt', dateFormat: 'mediumDate' },
  ];

  tableActionOptions: TableActionOptions = {
    showToggleButton: true,
    columnLabel: 'Action',
    useIconV2: true,
    toggleOptions: [
      {
        isButton: true,
        label: 'View Details',
        value: 'VIEW_DETAILS',
        iconName: 'eye',
        tooltip: 'View Details',
        isShow: true,
        color: '#A446FF',
      },
    ],
  };

  auditCategories = signal<AuditCategory[]>([]);
  conversationView = signal<ConversationViewModel | null>(null);

  filteredLeads = computed(() => this.leads());

  selectedLead = computed(() => {
    const list = this.filteredLeads();
    const idx = this.selectedLeadIndex();
    if (idx === null || idx < 0 || idx >= list.length) return null;
    return list[idx];
  });

  conversations = computed(() => {
    const lead = this.selectedLead();
    return [...(lead?.customerConversationEntries ?? [])].sort(
      (a, b) => this.getConversationSortTime(b) - this.getConversationSortTime(a)
    );
  });

  paginatedConversations = computed(() => {
    const start = (this.conversationPage() - 1) * this.conversationPageSize;
    return this.conversations().slice(start, start + this.conversationPageSize);
  });

  conversationTotalPages = computed(() =>
    Math.max(1, Math.ceil(this.conversations().length / this.conversationPageSize))
  );

  constructor() {
    effect(() => {
      this.filteredLeads();
      this.selectedLeadIndex.set(null);
      this.selectedConvIndex.set(null);
    }, { allowSignalWrites: true });

    effect(() => {
      const lead = this.selectedLead();
      const convIdx = this.selectedConvIndex();
      const convs = this.conversations();
      if (!lead || convs.length === 0 || convIdx === null) {
        this.conversationView.set(null);
        this.auditCategories.set([]);
        return;
      }
      const resolvedIndex = Math.min(convIdx, convs.length - 1);
      const conv = convs[resolvedIndex];
      const vm = this.buildConversationView(lead, conv);
      this.conversationView.set(vm);
      this.auditCategories.set(vm.auditCategories);
      this.activeDetailSection.set('recording');
    }, { allowSignalWrites: true });

    effect(() => {
      const sidebarPid = this.sidebarProductPid();
      if (this.lastSidebarProductPid === undefined) {
        return;
      }

      untracked(() => {
        if (sidebarPid === this.lastSidebarProductPid) {
          return;
        }

        this.lastSidebarProductPid = sidebarPid;
        this.currentPage.set(1);
        this.fetchLeads();
        if (this.activeCrmView() === 'CALL_HISTORY') {
          this.fetchOngoingCallsCount();
        }
      });
    }, { allowSignalWrites: true });

    effect(() => {
      const view = this.activeCrmView();
      untracked(() => {
        if (view === 'CALL_HISTORY') {
          this.fetchOngoingCallsCount();
          this.startOngoingCountPolling();
        } else {
          this.stopOngoingCountPolling();
        }
      });
    }, { allowSignalWrites: true });
  }

  ngOnInit(): void {
    this.setupAudioListeners();
    this.apiService.fetchProducts().subscribe({
      next: () => {
        this.lastSidebarProductPid = this.sidebarProductPid();
        this.fetchLeads();
      },
      error: () => {
        this.lastSidebarProductPid = this.sidebarProductPid();
        this.fetchLeads();
      },
    });
  }

  ngOnDestroy(): void {
    this.stopOngoingCountPolling();
    this.stopPlayback();
  }

  private fetchOngoingCallsCount(): void {
    const productPid = this.sidebarProductPid();
    const params = productPid != null ? { product_pid: productPid } : undefined;

    this.apiService
      .get<unknown>('/vocal-trans-eng/ongoing-calls', params, { showLoader: false })
      .subscribe({
        next: (res) => {
          const count = dedupeOngoingCalls(
            extractApiArray(res)
              .map(mapOngoingCallListItem)
              .filter((item): item is OngoingCallListItem => item != null)
          ).length;
          this.ongoingCallsCount.set(count);
        },
      });
  }

  private startOngoingCountPolling(): void {
    this.stopOngoingCountPolling();
    this.ongoingCountPollSub = interval(ONGOING_CALLS_REFRESH_MS).subscribe(() => {
      if (this.activeCrmView() === 'CALL_HISTORY') {
        this.fetchOngoingCallsCount();
      }
    });
  }

  private stopOngoingCountPolling(): void {
    this.ongoingCountPollSub?.unsubscribe();
    this.ongoingCountPollSub = null;
  }

  fetchLeads(): void {
    this.isLoading.set(true);
    this.error.set(null);

    let params: any = {
      page: this.currentPage(),
      limit: this.pageSize()
    };

    const startDate = this.startDateFilter();
    const endDate = this.endDateFilter();
    const productPid = this.sidebarProductPid();

    const searchString = this.searchQuery().trim();
    if (searchString) {
      params.searchString = searchString;
    }
    if (startDate) {
      params.start_date = startDate;
    }
    if (endDate) {
      params.end_date = endDate;
    }
    if (productPid != null) {
      params.product_pid = productPid;
    }
    const status = this.statusFilter();
    if (status !== 'ALL') {
      params.status = status;
    }
    params.isTest = this.isTest() ? true : false;

    this.apiService
      .get<any>('/vocal-trans-eng/all-leads-details', params, {
        preservePagination: true,
      })
      .subscribe({
        next: (res) => {
          const { leads: rawData, pagination } = this.extractLeadsPage(res);
          const mapped = rawData.map((lead: any) => this.mapLeadRow(lead));

          this.leads.set(mapped);
          this.totalItems.set(
            pagination?.totalItems ??
            pagination?.totalRecords ??
            pagination?.total ??
            mapped.length
          );
          this.serverSidePagination.set(true);
          const apiPage = Number(pagination?.currentPage);
          if (apiPage >= 1) {
            this.currentPage.set(apiPage);
          }

          this.isLoading.set(false);
          if (this.leads().length === 0) {
            this.error.set(null);
          }
        },
        error: () => {
          this.error.set(
            'Failed to load leads. Please check your connection and try again.'
          );
          this.isLoading.set(false);
        },
      });
  }

  private extractLeadsPage(res: any): { leads: any[]; pagination: any } {
    if (!res) {
      return { leads: [], pagination: null };
    }

    if (Array.isArray(res)) {
      return { leads: res, pagination: null };
    }

    if (Array.isArray(res.data)) {
      return { leads: res.data, pagination: res.pagination ?? null };
    }

    if (Array.isArray(res.results)) {
      return { leads: res.results, pagination: res.pagination ?? null };
    }

    if (Array.isArray(res.items)) {
      return { leads: res.items, pagination: res.pagination ?? null };
    }

    if (res.data && typeof res.data === 'object') {
      if (Array.isArray(res.data.data)) {
        return {
          leads: res.data.data,
          pagination: res.data.pagination ?? res.pagination ?? null,
        };
      }

      if (Array.isArray(res.data.results)) {
        return {
          leads: res.data.results,
          pagination: res.data.pagination ?? res.pagination ?? null,
        };
      }
    }

    return { leads: [], pagination: res.pagination ?? null };
  }

  private extractAudioDuration(res: any): number {
    if (res == null) {
      return 0;
    }

    const duration = res.duration ?? res.data?.duration ?? 0;
    const parsed = Number(duration);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }

  private mapLeadRow(lead: any): any {
    const productConfig = this.productConfigService.getProductConfig(
      lead.productName || lead.productKeyname || ''
    );
    const theme = productConfig.chatbotTheme;
    return {
      ...lead,
      businessName: lead.businessName || `Lead #${lead.customerPid}`,
      productName_class: theme
        ? `${theme.accentBg} ${theme.accentText}`
        : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
      platformType: this.formatPlatformType(lead.platformType),
      statusLabel: this.formatStatusLabel(lead.status),
      statusLabel_class: this.getStatusClass(lead.status),
    };
  }

  selectLead(index: number): void {
    this.selectedLeadIndex.set(index);
    this.conversationPage.set(1);

    // Auto-select first conversation if available
    const lead = this.filteredLeads()[index];
    if (lead?.customerConversationEntries && lead.customerConversationEntries.length > 0) {
      this.selectedConvIndex.set(0);
      this.activeDetailSection.set('recording');
    } else {
      this.selectedConvIndex.set(null);
    }
  }

  isConversationActive(pageIndex: number): boolean {
    const absoluteIndex = (this.conversationPage() - 1) * this.conversationPageSize + pageIndex;
    return this.selectedConvIndex() === absoluteIndex;
  }

  selectConversation(index: number): void {
    this.stopPlayback();
    this.activeDetailSection.set('recording');
    this.selectedConvIndex.set(index);
  }

  selectConversationFromPage(pageIndex: number): void {
    const absoluteIndex = (this.conversationPage() - 1) * this.conversationPageSize + pageIndex;
    this.selectConversation(absoluteIndex);
  }

  changeConversationPage(page: number): void {
    const nextPage = Math.min(Math.max(page, 1), this.conversationTotalPages());
    this.conversationPage.set(nextPage);
  }

  getConversationPageEnd(): number {
    return Math.min(this.conversationPage() * this.conversationPageSize, this.conversations().length);
  }

  onPageChange(page: number): void {
    this.currentPage.set(page);
    this.fetchLeads();
  }

  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    this.currentPage.set(1);
    this.fetchLeads();
  }

  onPendingDummyToggleChange(val: boolean): void {
    this.pendingIsTest.set(val);
  }

  onSearchQueryChange(value: string): void {
    this.pendingSearchQuery.set(value);
  }

  applyFilters(): void {
    this.searchQuery.set(this.pendingSearchQuery());
    this.startDateFilter.set(this.pendingStartDate());
    this.endDateFilter.set(this.pendingEndDate());
    this.statusFilter.set(this.pendingStatusFilter());
    this.isTest.set(this.pendingIsTest());
    this.currentPage.set(1);
    this.fetchLeads();
  }

  clearFilters(): void {
    this.pendingSearchQuery.set('');
    this.pendingStartDate.set(this.minFilterDate);
    this.pendingEndDate.set(this.maxFilterDate);
    this.pendingStatusFilter.set('ALL');
    this.pendingIsTest.set(false);
    this.searchQuery.set('');
    this.startDateFilter.set(this.minFilterDate);
    this.endDateFilter.set(this.maxFilterDate);
    this.statusFilter.set('ALL');
    this.isTest.set(false);
    this.currentPage.set(1);
    this.fetchLeads();
  }

  onPendingStatusChange(value: string | string[]): void {
    const selected = Array.isArray(value) ? value[0] : value;
    this.pendingStatusFilter.set((selected as CrmLeadStatusFilter) || 'ALL');
  }

  onPendingStartDateChange(value: string | string[] | null): void {
    const start = this.normalizeDateSelection(value);
    this.pendingStartDate.set(start);
    const end = this.pendingEndDate();
    if (start && end && end < start) {
      this.pendingEndDate.set(null);
    }
  }

  onPendingEndDateChange(value: string | string[] | null): void {
    this.pendingEndDate.set(this.normalizeDateSelection(value));
  }

  private normalizeDateSelection(value: string | string[] | null): string | null {
    const selected = Array.isArray(value) ? value[0] : value;
    return selected || null;
  }

  onActionSelected(event: { action: string; row: any }): void {
    if (event.action === 'VIEW_DETAILS') {
      const idx = this.filteredLeads().findIndex((l) => l.customerPid === event.row.customerPid);
      if (idx !== -1) {
        this.selectLead(idx);
      }
    }
  }

  backToList(): void {
    this.stopPlayback();
    this.activeDetailSection.set('recording');
    this.selectedLeadIndex.set(null);
  }

  selectDetailSection(section: CrmDetailSection): void {
    const currentIndex = this.detailSectionOrder.indexOf(this.activeDetailSection());
    const nextIndex = this.detailSectionOrder.indexOf(section);
    if (currentIndex !== -1 && nextIndex !== -1 && currentIndex !== nextIndex) {
      this.tabAnimationDirection.set(nextIndex > currentIndex ? 'right' : 'left');
    }
    this.activeDetailSection.set(section);
  }

  tabAnimationClass(): string {
    return this.tabAnimationDirection() === 'right'
      ? 'crm-tab-panel crm-tab-panel--from-right'
      : 'crm-tab-panel crm-tab-panel--from-left';
  }

  getLeadDisplayName(lead: any): string {
    return lead.businessName || `Lead #${lead.customerPid}`;
  }

  getTranscriptDownloadTitle(lead: any): string {
    return `${this.getLeadDisplayName(lead)} Call Transcription`;
  }

  getTranscriptDownloadFileName(lead: any): string {
    return `${this.getLeadDisplayName(lead).replace(/\s+/g, '_')}_Transcript.pdf`;
  }

  getStatusClass(status: string): string {
    const key = (status || '')
      .trim()
      .toUpperCase()
      .replace(/^SALES_PITCH/, 'SALE_PITCH');
    const map: Record<string, string> = {
      LEAD_ADDED: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      SALE_PITCH_FAILED: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      SALE_PITCH_SUCCESS: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    };
    return (
      map[key] ??
      'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
    );
  }

  scoreColor(score: number, isTenPointScale: boolean = false): string {
    const excellent = isTenPointScale ? 8 : 80;
    const average = isTenPointScale ? 5 : 50;
    if (score >= excellent) return 'bg-green-500';
    if (score >= average) return 'bg-amber-500';
    return 'bg-red-500';
  }

  scoreTextColor(score: number, isTenPointScale: boolean = false): string {
    const excellent = isTenPointScale ? 8 : 80;
    const average = isTenPointScale ? 5 : 50;
    if (score >= excellent) return 'text-green-600 dark:text-green-400';
    if (score >= average) return 'text-amber-600 dark:text-amber-400';
    return 'text-red-600 dark:text-red-400';
  }

  getInitials(name: string): string {
    if (!name) return '?';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  }

  formatStatusLabel(status: string): string {
    return (status || '').replace(/_/g, ' ');
  }

  private toTitleCase(value: string): string {
    return (value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
  }

  formatPlatformType(platformType: unknown): string {
    if (platformType == null || platformType === '' || platformType === 0) {
      return 'N.A.';
    }
    return String(platformType);
  }

  private getConversationSortTime(conversation: any): number {
    const dateSource =
      conversation?.conversationStartTime ||
      conversation?.createdAt ||
      conversation?.updatedAt ||
      '';
    const time = new Date(dateSource).getTime();
    return Number.isNaN(time) ? 0 : time;
  }

  private toBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (typeof value === 'string') {
      return ['true', '1', 'yes'].includes(value.trim().toLowerCase());
    }
    return false;
  }

  private setupAudioListeners(): void {
    this.audio.ontimeupdate = () => {
      if (this.isChangingAudioSource) return;
      const playing = this.activePlayingCall();
      if (!playing) return;
      const baseOffset = playing.baseOffset || 0;
      const trackStartOffset = playing.trackStartOffset || 0;
      const time = this.audio.currentTime + baseOffset + trackStartOffset;
      this.currentTime.set(time);
      this.updateActiveTranscriptIndex(time);
    };

    this.audio.onloadedmetadata = () => {
      const browserDuration = this.audio.duration;
      if (!Number.isFinite(browserDuration) || browserDuration <= 0) return;

      const playing = this.activePlayingCall();
      if (playing && playing.tracks && playing.tracks[playing.currentTrackIndex] != null) {
        playing.tracks[playing.currentTrackIndex].duration = browserDuration;
        const totalDuration = playing.tracks.reduce((sum: number, track: any) => sum + track.duration, 0);
        this.audioDuration.set(totalDuration);
      } else {
        const baseOffset = playing?.baseOffset || 0;
        if (baseOffset === 0) {
          this.audioDuration.set(browserDuration);
        } else {
          this.audioDuration.set(Math.max(this.audioDuration(), browserDuration + baseOffset));
        }
      }
    };

    this.audio.onended = () => {
      const playing = this.activePlayingCall();
      if (playing && playing.tracks && playing.currentTrackIndex + 1 < playing.tracks.length) {
        this.playTrackAtIndex(playing.currentTrackIndex + 1, 0);
      } else {
        this.isPlaying.set(false);
        this.activeTranscriptIndex.set(-1);
      }
    };
    this.audio.onplay = () => this.isPlaying.set(true);
    this.audio.onpause = () => this.isPlaying.set(false);
    this.audio.onerror = () => this.isPlaying.set(false);
  }

  private playTrackAtIndex(trackIndex: number, relativeStartTime: number): void {
    const playing = this.activePlayingCall();
    if (!playing || !playing.tracks || !playing.tracks[trackIndex]) return;

    playing.currentTrackIndex = trackIndex;
    playing.baseOffset = Math.floor(relativeStartTime);

    let trackStartOffset = 0;
    for (let i = 0; i < trackIndex; i++) {
      trackStartOffset += playing.tracks[i].duration || 0;
    }
    playing.trackStartOffset = trackStartOffset;

    const track = playing.tracks[trackIndex];
    const streamUrl = `${environment.apiUrl}/call-audit/stream-audio?url=${encodeURIComponent(track.url)}&productName=${encodeURIComponent(playing.productName || '')}&referer=${encodeURIComponent(playing.referer || '')}&startTime=${playing.baseOffset}`;

    this.isChangingAudioSource = true;
    this.audio.pause();
    this.audio.src = streamUrl;
    this.audio.load();
    this.audio.oncanplay = () => {
      this.audio.oncanplay = null;
      this.isChangingAudioSource = false;
      this.audio.play().catch(() => this.isPlaying.set(false));
    };
  }

  playConversation(vm: ConversationViewModel, startTime = 0, force = false): void {
    if (!vm.recording?.audioUrl) return;
    const activeCall = this.activePlayingCall();
    const isSameCall = activeCall?.conversationPid === vm.conversationPid;

    if (!force && isSameCall && startTime === 0 && this.isPlaying()) {
      this.togglePlay();
      return;
    }

    const tracks = vm.recording.tracks ? JSON.parse(JSON.stringify(vm.recording.tracks)) : [{
      url: vm.recording.audioUrl,
      duration: vm.recording.duration
    }];

    let targetTrackIndex = 0;
    let accumulatedTime = 0;
    let relativeStartTime = startTime;

    for (let i = 0; i < tracks.length; i++) {
      const trackDuration = tracks[i].duration || 0;
      if (startTime >= accumulatedTime && startTime < accumulatedTime + trackDuration) {
        targetTrackIndex = i;
        relativeStartTime = startTime - accumulatedTime;
        break;
      }
      accumulatedTime += trackDuration;
      if (i === tracks.length - 1) {
        targetTrackIndex = i;
        relativeStartTime = Math.max(0, startTime - (accumulatedTime - trackDuration));
      }
    }

    const recording = {
      ...vm.recording,
      conversationPid: vm.conversationPid,
      formattedTranscript: vm.formattedTranscript,
      tracks,
      currentTrackIndex: targetTrackIndex,
      trackStartOffset: accumulatedTime - (tracks[targetTrackIndex]?.duration || 0),
      baseOffset: Math.floor(relativeStartTime),
    };

    this.currentTime.set(startTime);
    this.activePlayingCall.set(recording);
    this.updateActiveTranscriptIndex(startTime);

    if (vm.recording.duration > 0) {
      this.audioDuration.set(vm.recording.duration);
    }

    this.playTrackAtIndex(targetTrackIndex, relativeStartTime);
  }

  togglePlay(): void {
    if (this.isPlaying()) {
      this.audio.pause();
      return;
    }

    const vm = this.conversationView();
    if (this.audio.ended || (this.audioDuration() > 0 && this.currentTime() >= this.audioDuration() - 0.1)) {
      if (vm) this.playConversation(vm, 0, true);
      return;
    }

    if (!this.activePlayingCall() && vm?.recording) {
      this.playConversation(vm, 0, true);
      return;
    }

    this.audio.play().catch(() => this.isPlaying.set(false));
  }

  toggleMute(): void {
    const newMutedState = !this.isMuted();
    this.audio.muted = newMutedState;
    this.isMuted.set(newMutedState);
  }

  seekTranscript(percent: number): void {
    const vm = this.conversationView();
    if (!vm?.recording || !this.audioDuration()) return;
    const targetTime = Math.max(0, Math.min(1, percent / 100)) * this.audioDuration();
    this.playConversation(vm, targetTime, true);
  }

  stopPlayback(): void {
    this.audio.pause();
    this.audio.removeAttribute('src');
    this.activePlayingCall.set(null);
    this.isPlaying.set(false);
    this.currentTime.set(0);
    this.audioDuration.set(0);
    this.activeTranscriptIndex.set(-1);
    this.isChangingAudioSource = false;
  }

  private fetchAudioDuration(recording: RecordingView): void {
    const params = {
      url: recording.audioUrl,
      productName: (recording.productName || '').toLowerCase(),
      referer: recording.referer || ''
    };

    this.apiService.get<any>('/call-audit/audio-duration', params).subscribe({
      next: (res) => {
        const duration = this.extractAudioDuration(res);
        if (duration > 0) {
          this.audioDuration.set(duration);
          this.activePlayingCall.update((call) => call ? { ...call, duration } : call);
        }
      },
    });
  }

  private updateActiveTranscriptIndex(time: number): void {
    const transcript = this.activePlayingCall()?.formattedTranscript || this.conversationView()?.formattedTranscript || [];
    let activeIndex = -1;

    for (let i = 0; i < transcript.length; i++) {
      const start = Number(transcript[i]?.timeSeconds ?? 0);
      const next = transcript[i + 1] ? Number(transcript[i + 1]?.timeSeconds ?? Infinity) : Infinity;
      if (time >= start && time < next) {
        activeIndex = i;
        break;
      }
    }

    this.activeTranscriptIndex.set(activeIndex);
  }

  private buildConversationView(lead: any, conv: any): ConversationViewModel {
    const auditEntry = conv.callAuditReport?.[0];
    const reportObj = this.parseJson(auditEntry?.report);
    const agentName = conv.agentDetails?.name || 'Agent';
    const agentEmail = conv.agentDetails?.email || '';
    const summaryLog = conv.summaryLogsEntries?.[0];
    const summaryLogView = this.buildSummaryLogView(summaryLog);

    const scoreFromEntries = (() => {
      if (Array.isArray(conv.callAuditRequestsEntries)) {
        for (const entry of conv.callAuditRequestsEntries) {
          const s = entry?.score ?? entry?.weightedScore ?? entry?.weighted_score;
          if (s != null && !isNaN(Number(s)) && Number(s) > 0) {
            return Number(s);
          }
        }
      }
      return null;
    })();

    const costingSources = [
      lead,
      conv,
      auditEntry,
      reportObj?.metadata,
      summaryLog,
      ...(Array.isArray(conv.callAuditRequestsEntries) ? conv.callAuditRequestsEntries : [])
    ];

    const emptyVm: ConversationViewModel = {
      conversationPid: conv.conversationPid,
      conversationStartTime: conv.conversationStartTime,
      conversationEndTime: conv.conversationEndTime,
      agentName,
      agentEmail,
      callType: auditEntry?.callType || '',
      platform: auditEntry?.platform || lead.platformType || '',
      overviewSummary: '',
      finalSummary: {
        weightedScore: scoreFromEntries ?? 0,
        highlights: [],
        performance: '',
        coaching: [],
        commentary: '',
        majorIssues: [],
      },
      callOverview: { contactType: '', summary: '', tone: '', callStatus: '' },
      bizIntel: {
        interestSignal: false,
        demoBooked: false,
        followUpRequested: false,
        industry: lead.industry || 'Unknown',
        sentiment: 'Unknown',
        callOutcome: 'Unknown',
        shortSummary: '',
      },
      topicAnalysis: { relevance: 'N/A', keyPoints: [] },
      auditCategories: [],
      formattedTranscript: this.normalizeTranscript(conv, lead, agentName),
      verdict: this.buildCallVerdict(auditEntry?.conversationInfo, lead.status),
      cheatSheet: this.buildLeadInsightSections(lead.customerSalePitchLogsEntries, 'cheat_sheet'),
      salesPitch: this.buildLeadInsightSections(lead.customerSalePitchLogsEntries, 'l1_sales_pitch'),
      costing: this.buildCostingView(costingSources),
      recording: this.buildRecordingView(lead, conv, auditEntry),
      hasAudit: !!reportObj,
      hasSummary: !!summaryLogView,
      summaryLogView,
    };

    if (!reportObj) {
      return emptyVm;
    }

    const overviewData = reportObj.call_overview?.data || [];
    const getOverviewNode = (seg: string) =>
      overviewData.find((d: any) => d.segment === seg)?.data;

    let overviewSummary = '';
    const summaryNode = overviewData.find((d: any) => d.segment === 'summary');
    if (summaryNode?.data) {
      overviewSummary = String(summaryNode.data);
    }

    const summaryData = reportObj.final_summary?.data || [];
    const getSummaryNode = (seg: string) =>
      summaryData.find((d: any) => d.segment === seg)?.data;

    const bizIntelData = reportObj.calls_business_intelligence?.data || [];
    const getBizIntelNode = (seg: string) =>
      bizIntelData.find((d: any) => d.segment === seg)?.data;

    const topicData = reportObj.topic_of_the_day_analysis?.data || [];
    const getTopicNode = (seg: string) =>
      topicData.find((d: any) => d.segment === seg)?.data;

    return {
      ...emptyVm,
      overviewSummary,
      finalSummary: {
        weightedScore:
          getSummaryNode('overall_weighted_score') ??
          getSummaryNode('weighted_score') ??
          scoreFromEntries ??
          0,
        highlights: getSummaryNode('key_highlights') || [],
        performance:
          getSummaryNode('overall_performance_evaluation') ??
          getSummaryNode('overall_performance') ??
          '',
        coaching: getSummaryNode('top_3_coaching_priorities') || [],
        commentary:
          getSummaryNode('commentary_on_topic_adherence') ||
          getSummaryNode('topic_adherence') ||
          '',
        majorIssues: getSummaryNode('major_issues') || [],
      },
      callOverview: {
        contactType: getOverviewNode('contact_type') || '',
        summary: getOverviewNode('summary') || '',
        tone: getOverviewNode('tone') || '',
        callStatus: getOverviewNode('call_status') || lead.status || '',
      },
      bizIntel: {
        interestSignal: this.toBoolean(getBizIntelNode('interest_signal')?.[0]),
        demoBooked: this.toBoolean(getBizIntelNode('demo_booked')?.[0]),
        followUpRequested: this.toBoolean(getBizIntelNode('follow_up_requested')?.[0]),
        industry: getBizIntelNode('industry')?.[0] || lead.industry || 'Unknown',
        sentiment: getBizIntelNode('sentiment')?.[0] || 'Unknown',
        callOutcome: getBizIntelNode('call_outcome')?.[0] || 'Unknown',
        shortSummary: getBizIntelNode('short_summary')?.[0] || '',
      },
      topicAnalysis: {
        relevance: getTopicNode('relevance') || 'N/A',
        keyPoints: getTopicNode('key_points') || [],
      },
      auditCategories: this.buildAuditCategories(reportObj, agentName, lead),
      verdict: this.buildCallVerdict(auditEntry?.conversationInfo, lead.status, {
        callOutcome: getBizIntelNode('call_outcome')?.[0],
        callStatus: getOverviewNode('call_status'),
      }),
      costing: this.buildCostingView(costingSources),
      recording: this.buildRecordingView(lead, conv, auditEntry),
      hasAudit: true,
      hasSummary: !!summaryLogView,
      summaryLogView,
    };
  }

  private buildSummaryLogView(summaryLog: any): SummaryLogView | null {
    if (!summaryLog || summaryLog.errorPayload) {
      return null;
    }

    const summary =
      typeof summaryLog.summary === 'string'
        ? this.parseJson(summaryLog.summary)
        : summaryLog.summary;
    const managementSummary = String(summaryLog.managementSummary || '').trim();
    const highlights = this.toStringArray(summary?.interest_signals);
    const majorIssues = [
      ...this.toStringArray(summary?.open_queries),
      ...(Array.isArray(summary?.objection_tracker)
        ? summary.objection_tracker
          .map((item: any) => item?.concern)
          .filter((concern: unknown) => typeof concern === 'string' && concern.trim())
        : []),
    ];
    const callFlow = Array.isArray(summary?.call_flow)
      ? summary.call_flow
        .map((item: any) => ({
          title: String(item?.title || '').trim(),
          summary: String(item?.summary || '').trim(),
        }))
        .filter((item: { title: string; summary: string }) => item.title || item.summary)
      : [];
    const discussionPoints = this.toStringArray(summary?.discussion_points);
    const prospectSnapshot = this.toStringArray(summary?.prospect_snapshot);

    const hasContent =
      managementSummary.length > 0 ||
      highlights.length > 0 ||
      majorIssues.length > 0 ||
      callFlow.length > 0 ||
      discussionPoints.length > 0 ||
      prospectSnapshot.length > 0;

    if (!hasContent) {
      return null;
    }

    return {
      managementSummary,
      highlights,
      majorIssues,
      callFlow,
      discussionPoints,
      prospectSnapshot,
    };
  }

  private toStringArray(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((item) => {
        if (typeof item === 'string') {
          return item.trim();
        }
        if (item && typeof item === 'object') {
          return Object.values(item)
            .filter((part) => typeof part === 'string' && part.trim())
            .join(' — ')
            .trim();
        }
        return '';
      })
      .filter((item) => item.length > 0);
  }

  private buildCallVerdict(
    conversationInfo: any,
    leadStatus: string,
    fallback: { callOutcome?: string; callStatus?: string } = {}
  ): CallVerdictView {
    const category = conversationInfo?.category || '';
    const reason = conversationInfo?.reason || fallback.callOutcome || fallback.callStatus || '';
    const label = category || fallback.callOutcome || fallback.callStatus || leadStatus || 'Unknown';
    const confidence =
      conversationInfo?.confidence != null && !Number.isNaN(Number(conversationInfo.confidence))
        ? Number(conversationInfo.confidence)
        : null;

    return {
      label: this.formatStatusLabel(label),
      reason,
      category,
      confidence,
    };
  }

  private buildLeadInsightSections(entries: any, key: string): CrmInsightSection[] {
    if (!Array.isArray(entries)) return [];

    return entries
      .map((entry, index) => {
        const value = entry?.[key];
        if (value == null || value === '') return [];
        const parsed = this.parseJson(value) ?? value;
        const sections = this.toInsightSections(parsed);
        const suffix = entries.length > 1 ? ` ${index + 1}` : '';
        return sections.length
          ? sections.map((section) => ({
            ...section,
            title: `${section.title}${suffix}`,
          }))
          : [
            {
              title: `${this.toTitleCase(key.replace(/_/g, ' '))}${suffix}`,
              content: String(value),
            },
          ];
      })
      .flat()
      .filter((section) => section.content.trim().length > 0);
  }

  private toInsightSections(value: any): CrmInsightSection[] {
    if (value == null) return [];
    if (typeof value === 'string') {
      return [{ title: 'Details', content: value }];
    }

    if (Array.isArray(value)) {
      return value.map((item, index) => ({
        title: `Item ${index + 1}`,
        content: this.formatInsightValue(item),
      }));
    }

    if (typeof value === 'object') {
      return Object.entries(value).map(([title, content]) => ({
        title: this.toTitleCase(title.replace(/_/g, ' ')),
        content: this.formatInsightValue(content),
      }));
    }

    return [{ title: 'Details', content: String(value) }];
  }

  private formatInsightValue(value: any): string {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);

    const isExcludedKey = (k: string) => {
      const lower = k.toLowerCase();
      return lower === 'evidence_source_ids' || lower === 'evidencesourceids' || lower === 'evidence_source_id' || lower === 'evidencesourceid';
    };

    if (Array.isArray(value)) {
      return value
        .map((item) =>
          typeof item === 'object' && item !== null
            ? Object.entries(item)
              .filter(([key]) => !isExcludedKey(key))
              .map(([key, val]) => `- **${this.toTitleCase(key.replace(/_/g, ' '))}:** ${this.formatInlineValue(val)}`)
              .join('\n')
            : `- ${this.formatInlineValue(item)}`
        )
        .join('\n');
    }
    return Object.entries(value)
      .filter(([key]) => !isExcludedKey(key))
      .map(([key, val]) => `- **${this.toTitleCase(key.replace(/_/g, ' '))}:** ${this.formatInlineValue(val)}`)
      .join('\n');
  }

  private formatInlineValue(value: any): string {
    if (value == null) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    return JSON.stringify(value);
  }

  private formatCostingValue(value: any): string {
    if (value == null || value === '') return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    if (Array.isArray(value)) {
      return value.map((item) => this.formatCostingValue(item)).filter(Boolean).join(', ');
    }
    if (typeof value === 'object') {
      const preferredKeys = ['name', 'model', 'provider', 'value', 'amount', 'total', 'currency'];
      const preferred = preferredKeys
        .map((key) => value[key])
        .filter((item) => item != null && item !== '')
        .map((item) => this.formatCostingValue(item));

      if (preferred.length) return preferred.join(' ');

      return Object.entries(value)
        .map(([key, val]) => {
          const formatted = this.formatCostingValue(val);
          return formatted ? `${this.toTitleCase(key.replace(/_/g, ' '))}: ${formatted}` : '';
        })
        .filter(Boolean)
        .join(', ');
    }
    return String(value);
  }

  private extractServicesWithPrices(sources: any[]): CostingServicePrice[] {
    const services: CostingServicePrice[] = [];

    const collectFromCheatSheet = (cheatSheet: any) => {
      const parsed = typeof cheatSheet === 'string' ? this.parseJson(cheatSheet) : cheatSheet;
      if (!parsed?.services_with_prices) return;

      const items = Array.isArray(parsed.services_with_prices)
        ? parsed.services_with_prices
        : [parsed.services_with_prices];

      items.forEach((item: any) => {
        if (!item || typeof item !== 'object') return;
        const service: CostingServicePrice = {
          serviceName: String(item.service_name ?? item.serviceName ?? '').trim(),
          price: String(item.price ?? '').trim(),
          source: String(item.source ?? '').trim(),
          confidence: String(item.confidence ?? '').trim(),
          pricePeriod: String(item.price_period ?? item.pricePeriod ?? '').trim(),
        };
        if (service.serviceName || service.price) {
          services.push(service);
        }
      });
    };

    sources.forEach((source) => {
      const entries = source?.customerSalePitchLogsEntries;
      if (Array.isArray(entries)) {
        entries.forEach((entry) => collectFromCheatSheet(entry?.cheat_sheet ?? entry?.cheatSheet));
      }
      collectFromCheatSheet(source?.cheat_sheet ?? source?.cheatSheet);
    });

    return services;
  }

  private buildCostingView(sources: any[]): CostingView | null {
    const rows = new Map<string, string>();
    const candidates: { key: string; value: any }[] = [];
    const servicesWithPrices = this.extractServicesWithPrices(sources);

    const visit = (value: any, depth = 0) => {
      if (!value || depth > 3) return;
      const parsed = typeof value === 'string' ? this.parseJson(value) : value;
      if (!parsed || typeof parsed !== 'object') return;

      Object.entries(parsed).forEach(([key, child]) => {
        const normalizedKey = key.toLowerCase();
        if (normalizedKey === 'services_with_prices') return;

        if (
          normalizedKey.includes('cost') ||
          normalizedKey.includes('price') ||
          normalizedKey.includes('amount') ||
          normalizedKey.includes('token') ||
          normalizedKey.includes('model') ||
          normalizedKey.includes('provider') ||
          normalizedKey === 'llm' ||
          normalizedKey === 'currency'
        ) {
          candidates.push({ key, value: child });
        }

        if (child && typeof child === 'object') {
          visit(child, depth + 1);
        }
      });
    };

    sources.forEach((source) => visit(source));

    console.log('--- buildCostingView debug ---');
    console.log('sources passed:', sources);
    console.log('candidates gathered:', candidates.map(c => ({ key: c.key, valueType: typeof c.value, preview: String(c.value).substring(0, 60) })));

    const getValue = (keys: string[]) => {
      const found = candidates.find(({ key, value }) => {
        const normalizedKey = key.toLowerCase();
        const matches = value != null && value !== '' && keys.some((k) => normalizedKey === k || normalizedKey.includes(k));
        if (matches) {
          console.log(`Matched key for ${keys}:`, { key, value });
        }
        return matches;
      });
      return found?.value;
    };

    const getNumericValue = (keys: string[]) => {
      const found = candidates.find(({ key, value }) => {
        const normalizedKey = key.toLowerCase();
        const matches = value != null && value !== '' && /\d/.test(String(value)) && keys.some((k) => normalizedKey === k || normalizedKey.includes(k));
        if (matches) {
          console.log(`Matched numeric key for ${keys}:`, { key, value });
        }
        return matches;
      });
      return found?.value;
    };

    const totalCost = getNumericValue(['totalcost', 'total_cost', 'costusd', 'cost', 'amount']);
    const currency = this.formatCostingValue(getValue(['currency'])) || 'USD';
    const provider = this.formatCostingValue(getValue(['provider', 'llm']));
    const model = this.formatCostingValue(getValue(['model']));
    const explicitTokens = getNumericValue(['tokensused', 'total_tokens', 'totaltokens', 'tokens']);
    const inputTokens = Number(getNumericValue(['input_tokens', 'inputtokens']) ?? 0);
    const outputTokens = Number(getNumericValue(['output_tokens', 'outputtokens']) ?? 0);
    const tokens = Number(explicitTokens ?? inputTokens + outputTokens);

    console.log('Resolved values:', { totalCost, currency, provider, model, tokens });

    const skipRowKeys = new Set(['services with prices', 'services_with_prices']);
    candidates.forEach(({ key, value }) => {
      if (value == null) return;
      const label = this.toTitleCase(key.replace(/_/g, ' '));
      if (skipRowKeys.has(label.toLowerCase())) return;
      const formatted = this.formatCostingValue(value);
      if (!formatted) return;
      rows.set(label, formatted);
    });

    const hasCosting =
      totalCost != null ||
      provider ||
      model ||
      (Number.isFinite(tokens) && tokens > 0) ||
      rows.size > 0 ||
      servicesWithPrices.length > 0;

    if (!hasCosting) return null;

    const formattedTotalCost = this.formatCostingValue(totalCost);
    const servicePrice = servicesWithPrices.find((s) => s.price)?.price ?? '';

    return {
      totalCost: formattedTotalCost,
      displayCost: formattedTotalCost,
      currency,
      provider,
      model,
      tokens: Number.isFinite(tokens) && tokens > 0 ? tokens : null,
      rows: Array.from(rows.entries()).map(([label, value]) => ({ label, value })),
      servicesWithPrices,
    };
  }

  private buildRecordingView(lead: any, conv: any, auditEntry: any): RecordingView | null {
    const auditRequestEntry = Array.isArray(conv.callAuditRequestsEntries)
      ? conv.callAuditRequestsEntries[0]
      : null;

    const tracks: AudioTrack[] = [];
    if (Array.isArray(conv.callAuditRequestsEntries)) {
      conv.callAuditRequestsEntries.forEach((entry: any) => {
        const url = entry?.s3url || entry?.s3Url;
        if (url) {
          tracks.push({
            url,
            duration: Number(entry.duration ?? entry.durationSeconds ?? entry.duration_seconds ?? 0) || 0
          });
        }
      });
    }

    if (tracks.length === 0) {
      const audioEntry = Array.isArray(conv.audioUrls)
        ? conv.audioUrls[0]
        : Array.isArray(auditEntry?.audioUrls)
          ? auditEntry.audioUrls[0]
          : null;
      const audioUrl =
        auditRequestEntry?.s3url ||
        auditRequestEntry?.s3Url ||
        conv.s3url ||
        conv.s3Url ||
        conv.audioUrl ||
        conv.recordingUrl ||
        conv.callRecordingUrl ||
        conv.recording?.s3url ||
        conv.recording?.s3Url ||
        conv.recording?.url ||
        audioEntry?.s3url ||
        audioEntry?.s3Url ||
        audioEntry?.signedUrl ||
        audioEntry?.url ||
        auditEntry?.s3url ||
        auditEntry?.s3Url ||
        auditEntry?.audioUrl ||
        auditEntry?.recordingUrl ||
        auditEntry?.callRecordingUrl ||
        '';

      if (audioUrl) {
        tracks.push({
          url: audioUrl,
          duration: Number(conv.duration ?? auditEntry?.duration ?? audioEntry?.duration ?? 0) || 0
        });
      }
    }

    if (tracks.length === 0) return null;

    const totalDuration = tracks.reduce((sum, track) => sum + track.duration, 0);

    return {
      audioUrl: tracks[0].url,
      tracks,
      referer:
        auditRequestEntry?.referer ||
        conv.meetLink ||
        auditEntry?.referer ||
        (Array.isArray(conv.audioUrls) ? conv.audioUrls[0]?.referer : null) ||
        (Array.isArray(auditEntry?.audioUrls) ? auditEntry.audioUrls[0]?.referer : null) ||
        '',
      duration: totalDuration,
      productName: lead.productName || lead.productKeyname || auditEntry?.productId || '',
      platform: auditEntry?.platform || lead.platformType || '',
    };
  }

  private buildAuditCategories(
    reportObj: any,
    agentName: string,
    lead: any
  ): AuditCategory[] {
    const rSA = reportObj?.segment_analysis;
    const isPopulated = (obj: any) => {
      if (!obj) return false;
      if (Array.isArray(obj)) return obj.length > 0;
      return Object.keys(obj).length > 0;
    };

    if (!isPopulated(rSA)) return [];

    let analysisArray: any[] = [];
    if (Array.isArray(rSA)) {
      analysisArray = rSA;
    } else if (Array.isArray(rSA.data)) {
      analysisArray = rSA.data;
    } else if (rSA.segment || rSA.display_name) {
      analysisArray = [rSA];
    }

    return analysisArray
      .filter(
        (seg) =>
          (seg.segment || seg.display_name) &&
          seg.segment !== 'weighted_score' &&
          (seg.segment !== 'score' || seg.display_name?.includes('Segment'))
      )
      .map((segmentData: any, index: number) => {
        const dataArr = segmentData.data || [];
        const findData = (seg: string) => {
          if (Array.isArray(dataArr)) {
            const found = dataArr.find((d: any) => d.segment === seg);
            return found ? found.data : null;
          }
          return dataArr[seg] ?? null;
        };

        // If the data is nested inside a single object in the data array (like the example JSON)
        const nestedData = Array.isArray(dataArr) && dataArr.length === 1 ? dataArr[0] : null;

        const title =
          segmentData.display_name ||
          segmentData.segment?.replace(/_/g, ' ') ||
          'Category';

        const excerpts = this.buildAuditExcerpts(
          findData,
          segmentData,
          nestedData,
          agentName,
          lead
        );

        return {
          id: index + 1,
          title: title.charAt(0).toUpperCase() + title.slice(1),
          score: findData('score') ?? findData('weighted_score') ?? segmentData.score ?? segmentData.weighted_score ?? nestedData?.score ?? nestedData?.weighted_score ?? 0,
          justification: findData('score_note') || findData('justification') || segmentData.score_note || segmentData.justification || nestedData?.score_note || nestedData?.justification || '',
          analysis: findData('analysis') || segmentData.analysis || nestedData?.analysis || '',
          analysisSub:
            findData('summary_insight') || segmentData.summary_insight || nestedData?.summary_insight || '',
          wentWell: Array.isArray(findData('what_went_well'))
            ? findData('what_went_well')
            : (Array.isArray(segmentData.what_went_well) ? segmentData.what_went_well : (Array.isArray(nestedData?.what_went_well) ? nestedData.what_went_well : [])),
          opportunities: Array.isArray(findData('areas_for_improvement'))
            ? findData('areas_for_improvement')
            : (Array.isArray(segmentData.areas_for_improvement) ? segmentData.areas_for_improvement : (Array.isArray(nestedData?.areas_for_improvement) ? nestedData.areas_for_improvement : [])),
          excerpts,
        };
      });
  }

  private buildAuditExcerpts(
    findData: (seg: string) => any,
    segmentData: any,
    nestedData: any,
    agentName: string,
    _lead: any
  ): AuditExcerpt[] {
    const mapped: AuditExcerpt[] = [];

    const snippetSource =
      findData('conversation_snippet') ??
      segmentData.conversation_snippet ??
      nestedData?.conversation_snippet;

    if (Array.isArray(snippetSource)) {
      snippetSource.forEach((line: any, i: number) => {
        if (!line?.text) return;
        const roleRaw = (line.role ?? '').toString().toLowerCase();
        const speaker = (line.speaker ?? '').toString();
        const isAgent =
          ['advisor', 'agent', 'salesagent'].includes(roleRaw) ||
          (agentName && speaker.toLowerCase().includes(agentName.toLowerCase()));
        const isAI = ['ai', 'llm', 'assistant'].includes(roleRaw);
        const time = line.time || line.formattedTime || '';

        mapped.push({
          label: i === 0 ? 'Context' : isAgent ? 'Current performance' : 'Context',
          text: speaker ? `${speaker}: "${line.text}"` : `"${line.text}"`,
          time,
          role: isAI ? 'AI' : isAgent ? 'AGENT' : 'CUSTOMER',
        });
      });
    } else if (typeof snippetSource === 'string' && snippetSource.trim()) {
      const lines = snippetSource.split('\n').filter((l: string) => l.trim());
      lines.forEach((line: string, i: number) => {
        const match = line.match(/^\[(\d{2}:\d{2})\]\s+([^:]+):\s+(.*)$/);
        if (match) {
          const speaker = match[2];
          const text = match[3];
          const speakerLower = speaker.toLowerCase();
          const isAgent =
            ['advisor', 'agent', 'salesagent'].includes(speakerLower) ||
            (agentName && speakerLower.includes(agentName.toLowerCase()));
          const isAI = ['ai', 'llm', 'assistant'].includes(speakerLower);
          mapped.push({
            label: i === 0 ? 'Context' : isAgent ? 'Current performance' : 'Context',
            text: `${speaker} said: "${text}"`,
            time: match[1],
            role: isAI ? 'AI' : isAgent ? 'AGENT' : 'CUSTOMER',
          });
        } else {
          mapped.push({
            label: 'Excerpt',
            text: line,
            role: 'CUSTOMER',
          });
        }
      });
    }

    const recommendations =
      findData('recommendations') ??
      segmentData.recommendations ??
      nestedData?.recommendations;
    if (Array.isArray(recommendations) && recommendations.length > 0) {
      mapped.push({
        label: 'Recommended approach',
        text: String(recommendations[0]),
        role: 'AI',
      });
    }

    return mapped;
  }

  private getScorePdfRgb(score: number, tenPointScale = false): [number, number, number] {
    if (tenPointScale) {
      if (score >= 8) return [34, 197, 94];
      if (score >= 5) return [245, 158, 11];
      return [239, 68, 68];
    }
    if (score >= 80) return [34, 197, 94];
    if (score >= 50) return [245, 158, 11];
    return [239, 68, 68];
  }

  private normalizeTranscript(
    conv: any,
    lead: any,
    agentName: string
  ): FormattedTranscriptLine[] {
    const auditEntry = conv.callAuditReport?.[0];
    const deepgram = this.parseJson(auditEntry?.conversation);
    if (Array.isArray(deepgram) && deepgram.length > 0 && deepgram[0].transcript) {
      return deepgram.map((utterance: any) => {
        const seconds = Math.floor(utterance.start ?? 0);
        const speakerNum = utterance.speaker ?? 0;
        const isAgent = speakerNum === 0;
        return {
          timeSeconds: seconds,
          formattedTime: this.formatTime(seconds),
          time: this.formatTime(seconds),
          sender: isAgent ? agentName || 'Agent' : 'Customer',
          speaker: isAgent ? agentName || 'Agent' : 'Customer',
          role: isAgent ? 'AGENT' : 'CUSTOMER',
          content: utterance.transcript || '',
          text: utterance.transcript || '',
        };
      });
    }

    const full = conv.fullTranscription;
    if (!Array.isArray(full) || full.length === 0) return [];

    const startTs = full[0]?.timestamp;
    const baseMs = startTs ? new Date(startTs).getTime() : 0;

    return full
      .filter((msg: any) => msg.text || msg.content || msg.transcript)
      .map((msg: any) => {
        let seconds = 0;
        if (msg.timestamp != null && baseMs) {
          seconds = Math.max(
            0,
            Math.floor((new Date(msg.timestamp).getTime() - baseMs) / 1000)
          );
        } else if (typeof msg.start === 'number') {
          seconds = Math.floor(msg.start);
        }

        const speaker = (msg.speaker ?? msg.role ?? '').toString();
        const speakerLower = speaker.toLowerCase();
        const isAgent =
          ['advisor', 'agent', 'salesagent', '0'].includes(speakerLower) ||
          msg.role === 'AGENT' ||
          msg.role === 'agent';
        const isAI = ['ai', 'llm', 'assistant'].includes(speakerLower);

        const sender = isAI
          ? 'AI'
          : isAgent
            ? agentName || 'Agent'
            : lead.businessName || 'Customer';

        return {
          timeSeconds: seconds,
          formattedTime: this.formatTime(seconds),
          time: this.formatTime(seconds),
          sender: sender,
          speaker: sender,
          role: isAI ? 'AI' : isAgent ? 'AGENT' : 'CUSTOMER',
          content: msg.text || msg.content || msg.transcript || '',
          text: msg.text || msg.content || msg.transcript || '',
        };
      });
  }

  private parseJson(value: any): any {
    if (!value) return null;
    if (typeof value === 'object') return value;
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    }
    return null;
  }

  private formatTime(seconds: number): string {
    if (!isFinite(seconds) || seconds < 0) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  downloadTranscript() {
    const vm = this.conversationView();
    const lead = this.selectedLead();
    if (!vm || !vm.formattedTranscript || !lead) return;

    const doc = new jsPDF();
    let y = 20;
    const margin = 20;

    doc.setFontSize(22);
    doc.setTextColor(147, 51, 234);
    doc.text('Call Transcription', margin, y);
    y += 15;

    doc.setFontSize(12);
    doc.setTextColor(15, 23, 42);
    doc.text(`Business: ${this.getLeadDisplayName(lead)}`, margin, y); y += 8;
    doc.text(`Date: ${vm.conversationStartTime ? new Date(vm.conversationStartTime).toLocaleString() : 'Unknown'}`, margin, y); y += 15;

    doc.setFontSize(10);

    vm.formattedTranscript.forEach((msg: any) => {
      if (y > 270) { doc.addPage(); y = 20; }

      doc.setFont("helvetica", 'bold');
      if (msg.role === 'AGENT') {
        doc.setTextColor(79, 70, 229); // Indigo 600
      } else if (msg.role === 'CUSTOMER') {
        doc.setTextColor(37, 99, 235); // Blue 600
      } else if (msg.role === 'AI') {
        doc.setTextColor(147, 51, 234); // Purple 600
      } else {
        doc.setTextColor(100, 116, 139); // Slate 500
      }
      doc.text(`[${msg.time}] ${msg.speaker}:`, margin, y); y += 6;

      doc.setFont("helvetica", 'normal');
      doc.setTextColor(15, 23, 42);
      const lines = doc.splitTextToSize(msg.text, 170);

      if (y + (lines.length * 6) > 280) {
        doc.addPage(); y = 20;
      }

      doc.text(lines, margin, y);
      y += (lines.length * 6) + 4;
    });

    doc.save(`${this.getLeadDisplayName(lead).replace(/\s+/g, '_')}_Transcript.pdf`);
  }

  downloadReport() {
    const vm = this.conversationView();
    const lead = this.selectedLead();
    if (!vm || !lead) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 18;
    const contentWidth = pageWidth - margin * 2;
    const bottomMargin = 22;
    const topMargin = 28;
    let y = topMargin;

    const convIdx = this.selectedConvIndex();
    const conv =
      convIdx !== null ? this.conversations()[convIdx] : this.conversations()[0];
    const auditEntry = conv?.callAuditReport?.[0];
    const conversationInfo = auditEntry?.conversationInfo;
    const summaryLog = conv?.summaryLogsEntries?.[0];
    const reportDate = this.getReportDate(vm, lead);
    const subtitle = `Business: ${this.getLeadDisplayName(lead)} | Agent: ${vm.agentName} | Date: ${reportDate}`;
    const summary = vm.finalSummary;

    const ensureSpace = (height: number) => {
      if (y + height > pageHeight - bottomMargin) {
        doc.addPage();
        y = topMargin;
      }
    };

    const sectionTitle = (title: string, color: [number, number, number] = [15, 23, 42]) => {
      ensureSpace(14);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(...color);
      doc.text(title, margin, y);
      y += 8;
    };

    const paragraph = (
      text: string,
      options: {
        x?: number;
        width?: number;
        fontSize?: number;
        lineHeight?: number;
        color?: [number, number, number];
        fontStyle?: 'normal' | 'bold' | 'italic';
      } = {}
    ) => {
      if (!text) return;
      const x = options.x ?? margin;
      const width = options.width ?? contentWidth;
      const lineHeight = options.lineHeight ?? 5.5;
      doc.setFont('helvetica', options.fontStyle ?? 'normal');
      doc.setFontSize(options.fontSize ?? 10);
      doc.setTextColor(...(options.color ?? [51, 65, 85]));
      const lines = doc.splitTextToSize(this.stripMarkdown(text), width);
      lines.forEach((line: string) => {
        ensureSpace(lineHeight);
        doc.text(line, x, y);
        y += lineHeight;
      });
    };

    const boxedParagraph = (
      text: string,
      options: {
        fill: [number, number, number];
        stroke?: [number, number, number];
        textColor?: [number, number, number];
      }
    ) => {
      if (!text) return;
      const padding = 5;
      const lineHeight = 5.5;
      const lines = doc.splitTextToSize(this.stripMarkdown(text), contentWidth - padding * 2);
      const boxHeight = lines.length * lineHeight + padding * 2;
      ensureSpace(boxHeight + 3);
      doc.setFillColor(...options.fill);
      if (options.stroke) {
        doc.setDrawColor(...options.stroke);
        doc.roundedRect(margin, y, contentWidth, boxHeight, 2, 2, 'FD');
      } else {
        doc.roundedRect(margin, y, contentWidth, boxHeight, 2, 2, 'F');
      }
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);
      doc.setTextColor(...(options.textColor ?? [15, 23, 42]));
      lines.forEach((line: string, index: number) => {
        doc.text(line, margin + padding, y + padding + 4 + index * lineHeight);
      });
      y += boxHeight + 6;
    };

    const bulletList = (
      items: string[],
      options: { color?: [number, number, number]; marker?: string } = {}
    ) => {
      const marker = options.marker ?? '•';
      const color = options.color ?? [51, 65, 85];
      (items || []).forEach((item) => {
        const lines = doc.splitTextToSize(this.stripMarkdown(item), contentWidth - 8);
        ensureSpace(lines.length * 5.5 + 2);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9.5);
        doc.setTextColor(...color);
        doc.text(marker, margin, y);
        lines.forEach((line: string, index: number) => {
          doc.text(line, margin + 6, y + index * 5.5);
        });
        y += lines.length * 5.5 + 2;
      });
    };

    const renderRecommendedApproach = (text: string) => {
      const cleanText = this.stripMarkdown(text);
      const recommendationMatch = cleanText.match(
        /Consultant should have replied:\s*->\s*([\s\S]*)/i
      );
      const recommendation = recommendationMatch?.[1]
        ?.replace(/^['"]|['"]$/g, '')
        .trim();
      const context = recommendationMatch
        ? cleanText.slice(0, recommendationMatch.index).trim()
        : cleanText;
      const contextLinesRaw = context.split(/\r?\n/).filter((line) => line.trim());
      const contextTitle = contextLinesRaw[0] || 'Example context';
      const contextBody = contextLinesRaw.slice(1).join('\n');

      const renderLabeledBox = (
        label: string,
        body: string,
        options: {
          labelColor: [number, number, number];
          fill: [number, number, number];
          textColor: [number, number, number];
          fontSize?: number;
        }
      ) => {
        if (!body) return;
        const padding = 5;
        const lineHeight = 5.3;
        const lines = doc.splitTextToSize(body, contentWidth - padding * 2);
        const boxHeight = lines.length * lineHeight + padding * 2;

        ensureSpace(boxHeight + 14);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(...options.labelColor);
        doc.text(label.toUpperCase(), margin, y);
        y += 5;

        doc.setFillColor(...options.fill);
        doc.roundedRect(margin, y, contentWidth, boxHeight, 2, 2, 'F');
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(options.fontSize ?? 9);
        doc.setTextColor(...options.textColor);
        lines.forEach((line: string, index: number) => {
          doc.text(line, margin + padding, y + padding + 4 + index * lineHeight);
        });
        y += boxHeight + 6;
      };

      renderLabeledBox(contextTitle, contextBody || contextTitle, {
        labelColor: [100, 116, 139],
        fill: [248, 250, 252],
        textColor: [71, 85, 105],
        fontSize: 8.8,
      });

      renderLabeledBox('Suggested response', recommendation || cleanText, {
        labelColor: [21, 128, 61],
        fill: [240, 253, 244],
        textColor: [21, 128, 61],
        fontSize: 9.3,
      });
    };

    // Cover / executive summary
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(147, 51, 234);
    doc.text('Call Quality Report', margin, y);
    y += 10;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    paragraph(subtitle, { fontSize: 9, lineHeight: 4.8, color: [100, 116, 139] });
    y += 5;

    sectionTitle('Executive Summary');
    const [scoreR, scoreG, scoreB] = this.getScorePdfRgb(summary.weightedScore, false);
    ensureSpace(24);
    doc.setFillColor(scoreR, scoreG, scoreB);
    doc.roundedRect(margin, y, 22, 22, 4, 4, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text(`${summary.weightedScore}`, margin + 11, y + 10, { align: 'center' });
    doc.setFontSize(7);
    doc.text('SCORE', margin + 11, y + 17, { align: 'center' });
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(13);
    doc.text('Overall Weighted Score', margin + 28, y + 9);
    if (vm.callOverview.callStatus || vm.bizIntel.callOutcome) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(71, 85, 105);
      doc.text(
        `Outcome: ${vm.bizIntel.callOutcome || vm.callOverview.callStatus}`,
        margin + 28,
        y + 16
      );
    }
    y += 30;

    if (vm.overviewSummary || vm.callOverview.summary) {
      boxedParagraph(vm.overviewSummary || vm.callOverview.summary, {
        fill: [248, 250, 252],
        textColor: [51, 65, 85],
      });
    }

    if (summary.highlights?.length) {
      sectionTitle('Key Highlights', [34, 197, 94]);
      bulletList(summary.highlights, { color: [51, 65, 85] });
      y += 3;
    }

    if (summary.majorIssues?.length) {
      sectionTitle('Major Issues', [239, 68, 68]);
      bulletList(summary.majorIssues, { color: [71, 85, 105] });
      y += 3;
    }

    if (summary.performance) {
      sectionTitle('Overall Performance Evaluation');
      boxedParagraph(summary.performance, {
        fill: [248, 250, 252],
        textColor: [15, 23, 42],
      });
    }

    if (summary.coaching?.length) {
      sectionTitle('Top 3 Coaching Priorities', [180, 83, 9]);
      bulletList(summary.coaching.map((item, index) => `${index + 1}. ${item}`), {
        marker: '',
        color: [120, 53, 15],
      });
      y += 3;
    }

    const intelRows = [
      vm.bizIntel.sentiment && `Sentiment: ${vm.bizIntel.sentiment}`,
      vm.bizIntel.shortSummary && `Short summary: ${vm.bizIntel.shortSummary}`,
      vm.topicAnalysis.relevance && `Topic relevance: ${vm.topicAnalysis.relevance}`,
      conversationInfo?.category && `Conversation category: ${conversationInfo.category}`,
      conversationInfo?.confidence != null && `Conversation confidence: ${conversationInfo.confidence}`,
      conversationInfo?.reason && `Reason: ${conversationInfo.reason}`,
    ].filter(Boolean) as string[];

    if (intelRows.length || vm.topicAnalysis.keyPoints?.length || summaryLog?.managementSummary) {
      sectionTitle('Additional Intelligence');
      bulletList(intelRows);
      if (vm.topicAnalysis.keyPoints?.length) {
        paragraph('Topic key points:', { fontStyle: 'bold', color: [71, 85, 105] });
        bulletList(vm.topicAnalysis.keyPoints);
      }
      if (summaryLog?.managementSummary) {
        paragraph('Management summary:', { fontStyle: 'bold', color: [71, 85, 105] });
        boxedParagraph(summaryLog.managementSummary, {
          fill: [248, 250, 252],
          textColor: [51, 65, 85],
        });
      }
    }

    const categories = this.auditCategories();
    if (categories.length) {
      doc.addPage();
      y = topMargin;
    }

    categories.forEach((cat, index) => {
      ensureSpace(32);
      const [catR, catG, catB] = this.getScorePdfRgb(cat.score, true);
      doc.setFillColor(catR, catG, catB);
      doc.roundedRect(margin, y, 16, 16, 3, 3, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text(`${cat.score}`, margin + 8, y + 7, { align: 'center' });
      doc.setFontSize(6);
      doc.text('SCORE', margin + 8, y + 12, { align: 'center' });

      doc.setTextColor(15, 23, 42);
      doc.setFontSize(14);
      const titleLines = doc.splitTextToSize(cat.title, contentWidth - 24);
      titleLines.forEach((line: string, lineIndex: number) => {
        doc.text(line, margin + 22, y + 7 + lineIndex * 6);
      });
      y += Math.max(22, titleLines.length * 6 + 8);

      if (cat.justification) {
        sectionTitle('Justification', [71, 85, 105]);
        boxedParagraph(cat.justification, {
          fill: [248, 250, 252],
          textColor: [15, 23, 42],
        });
      }

      if (cat.analysis) {
        sectionTitle('Analysis & Insight', [71, 85, 105]);
        paragraph(`"${cat.analysis}"`, {
          fontStyle: 'italic',
          fontSize: 10,
          lineHeight: 5.5,
          color: [15, 23, 42],
        });
        y += 2;
      }

      if (cat.analysisSub) {
        paragraph(cat.analysisSub, {
          fontSize: 9.5,
          lineHeight: 5.5,
          color: [71, 85, 105],
        });
        y += 3;
      }

      if (cat.excerpts?.length) {
        sectionTitle('Conversation Excerpts', [71, 85, 105]);
        cat.excerpts.forEach((ex) => {
          const isRecommendation = ex.label === 'Recommended approach';
          ensureSpace(16);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8);
          doc.setTextColor(isRecommendation ? 21 : 148, isRecommendation ? 128 : 163, isRecommendation ? 61 : 184);
          doc.text(ex.label.toUpperCase(), margin, y);
          y += 5;
          if (isRecommendation) {
            renderRecommendedApproach(ex.text);
          } else {
            paragraph(ex.text, {
              fontSize: 9.5,
              lineHeight: 5.5,
              color:
                ex.role === 'AGENT'
                  ? [79, 70, 229]
                  : ex.role === 'CUSTOMER'
                    ? [37, 99, 235]
                    : [71, 85, 105],
            });
            y += 2;
          }
        });
      }

      if (cat.wentWell?.length) {
        sectionTitle('What Went Well', [34, 197, 94]);
        bulletList(cat.wentWell, { color: [22, 101, 52] });
        y += 2;
      }

      if (cat.opportunities?.length) {
        sectionTitle('Opportunities', [245, 158, 11]);
        bulletList(cat.opportunities, { color: [120, 53, 15] });
      }

      if (index < categories.length - 1) {
        ensureSpace(14);
        doc.setDrawColor(226, 232, 240);
        doc.line(margin, y + 4, pageWidth - margin, y + 4);
        y += 14;
      }
    });

    this.addReportPageFooter(doc);
    doc.save(`${this.getLeadDisplayName(lead).replace(/\s+/g, '_')}_Call_Report.pdf`);
  }

  private getReportDate(vm: ConversationViewModel, lead: any): string {
    const dateSource =
      vm.conversationStartTime ||
      lead?.customerCreatedAt ||
      lead?.customerUpdatedAt ||
      null;

    if (!dateSource) return 'Unknown';

    const date = new Date(dateSource);
    if (Number.isNaN(date.getTime())) return 'Unknown';

    return date.toLocaleString();
  }

  private stripMarkdown(text: string): string {
    return String(text ?? '')
      .replace(/\*\*/g, '')
      .replace(/`/g, '')
      .replace(/\[(.*?)\]\((.*?)\)/g, '$1')
      .replace(/^\s*[-*]\s+/gm, '• ')
      .trim();
  }

  private addReportPageFooter(doc: any): void {
    const pageCount = doc.getNumberOfPages();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    for (let page = 1; page <= pageCount; page++) {
      doc.setPage(page);
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(`Page ${page} of ${pageCount}`, pageWidth - 18, pageHeight - 10, {
        align: 'right',
      });
    }
  }

  private downloadReportLegacy() {
    const vm = this.conversationView();
    const lead = this.selectedLead();
    if (!vm || !lead) return;

    const doc = new jsPDF();
    let y = 20;
    const margin = 20;
    const colWidth = 80;
    const colGap = 10;
    const leftColX = margin;
    const rightColX = margin + colWidth + colGap;

    // Header
    doc.setFontSize(22);
    doc.setTextColor(147, 51, 234); // Purple 600
    doc.setFont("helvetica", "bold");
    doc.text('Call Quality Report', margin, y);
    y += 12;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 116, 139); // Slate 500
    const dateStr = vm.conversationStartTime ? new Date(vm.conversationStartTime).toLocaleString() : 'Unknown';
    doc.text(`Business: ${this.getLeadDisplayName(lead)} | Agent: ${vm.agentName} | Date: ${dateStr}`, margin, y);
    y += 8;

    // --- FINAL SUMMARY SECTION ---
    const summary = vm.finalSummary;
    if (summary) {
      doc.setDrawColor(226, 232, 240);
      doc.line(margin, y, 190, y);
      y += 15;

      doc.setFontSize(16);
      doc.setTextColor(15, 23, 42);
      doc.setFont("helvetica", "bold");
      doc.text('FINAL SUMMARY', margin, y);
      y += 15;

      // Overall Score Badge
      const [wR, wG, wB] = this.getScorePdfRgb(summary.weightedScore, false);
      doc.setFillColor(wR, wG, wB);
      doc.roundedRect(margin, y, 20, 20, 4, 4, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.text(`${summary.weightedScore}`, margin + 10, y + 10, { align: 'center' });
      doc.setFontSize(8);
      doc.text('SCORE', margin + 10, y + 16, { align: 'center' });

      doc.setTextColor(15, 23, 42);
      doc.setFontSize(14);
      doc.text('Overall Weighted Score', margin + 25, y + 11);
      y += 30;

      // Key Highlights
      if (summary.highlights && summary.highlights.length > 0) {
        doc.setFontSize(11);
        doc.setTextColor(34, 197, 94); // Green 500
        doc.text('KEY HIGHLIGHTS', margin, y);
        y += 8;
        doc.setFontSize(9);
        doc.setTextColor(71, 85, 105);
        doc.setFont("helvetica", "normal");
        summary.highlights.forEach((h: string) => {
          const lines = doc.splitTextToSize(`• ${h}`, 170);
          if (y + (lines.length * 5) > 280) { doc.addPage(); y = 20; }
          doc.text(lines, margin, y);
          y += (lines.length * 5) + 3;
        });
        y += 5;
      }

      // Performance Evaluation
      if (summary.performance) {
        doc.setFontSize(11);
        doc.setTextColor(71, 85, 105);
        doc.setFont("helvetica", "bold");
        doc.text('OVERALL PERFORMANCE EVALUATION', margin, y);
        y += 6;

        doc.setFillColor(248, 250, 252);
        const perfLines = doc.splitTextToSize(summary.performance, 160);
        const perfBoxH = (perfLines.length * 5) + 10;
        if (y + perfBoxH > 280) { doc.addPage(); y = 20; }
        doc.roundedRect(margin, y, 170, perfBoxH, 2, 2, 'F');
        doc.setTextColor(15, 23, 42);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text(perfLines, margin + 5, y + 8);
        y += perfBoxH + 15;
      }

      // Coaching Priorities
      if (summary.coaching && summary.coaching.length > 0) {
        doc.setFontSize(11);
        doc.setTextColor(71, 85, 105);
        doc.setFont("helvetica", "bold");
        doc.text('TOP 3 COACHING PRIORITIES', margin, y);
        y += 8;

        summary.coaching.forEach((c: string) => {
          const plainText = c.replace(/\*\*/g, '');
          doc.setFont("helvetica", "bold");
          const cLines = doc.splitTextToSize(plainText, 160);
          const cBoxH = (cLines.length * 5) + 12;
          if (y + cBoxH > 280) { doc.addPage(); y = 20; }

          doc.setDrawColor(245, 158, 11); // Amber 500 border
          doc.setFillColor(255, 251, 235); // Amber 50
          doc.roundedRect(margin, y, 170, cBoxH, 2, 2, 'FD');

          doc.setTextColor(180, 83, 9); // Amber 800
          doc.setFontSize(9);
          this.renderMarkdownPDF(doc, c, margin + 5, y + 8, 160);
          y += cBoxH + 5;
        });
        y += 10;
      }

      // Add a separator before categories
      doc.setDrawColor(226, 232, 240);
      doc.line(margin, y, 190, y);
      y += 15;
    }

    this.auditCategories().forEach((cat, index) => {
      // Calculate estimated height to check for page break
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      const justificationLines = doc.splitTextToSize(cat.justification, colWidth - 10);

      doc.setFontSize(10);
      doc.setFont("helvetica", "italic");
      const analysisLines = doc.splitTextToSize(`"${cat.analysis}"`, colWidth);
      let analysisSubLines: any[] = [];
      if (cat.analysisSub) {
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        analysisSubLines = doc.splitTextToSize(cat.analysisSub, colWidth);
      }

      doc.setFontSize(9);
      const excerptHeight = (cat.excerpts?.length || 0) * 12; // Approximation

      const wwLinesCount = (cat.wentWell || []).reduce((acc: number, item: string) => acc + doc.splitTextToSize(`• ${item}`, colWidth).length, 0);
      const oppLinesCount = (cat.opportunities || []).reduce((acc: number, item: string) => acc + doc.splitTextToSize(`• ${item}`, colWidth).length, 0);
      const listHeight = Math.max(wwLinesCount, oppLinesCount) * 7 + 15;

      const leftHeight = 20 + (justificationLines.length * 5) + 15 + (analysisLines.length * 5) + (analysisSubLines.length > 0 ? (analysisSubLines.length * 5) + 5 : 0);
      const rightHeight = 10 + excerptHeight;
      const sectionHeight = Math.max(leftHeight, rightHeight) + listHeight + 20;

      if (y + sectionHeight > 280) {
        doc.addPage();
        y = 20;
      }

      // 1. Score Badge & Title
      const [sR, sG, sB] = this.getScorePdfRgb(cat.score, true);
      doc.setFillColor(sR, sG, sB);
      doc.roundedRect(leftColX, y, 15, 15, 3, 3, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text(`${cat.score}`, leftColX + 7.5, y + 7, { align: 'center' });
      doc.setFontSize(8);
      doc.text('SCORE', leftColX + 7.5, y + 12, { align: 'center' });

      doc.setTextColor(15, 23, 42); // Slate 900
      doc.setFontSize(14);
      const titleLines = doc.splitTextToSize(cat.title, colWidth - 20);
      doc.text(titleLines, leftColX + 20, y + 6);

      y += 20;
      const columnsTopY = y;

      // --- LEFT COLUMN ---
      doc.setFontSize(9);
      doc.setTextColor(71, 85, 105); // Slate 600
      doc.setFont("helvetica", "bold");
      doc.text('JUSTIFICATION', leftColX, y);
      y += 5;

      // Justification Box
      doc.setFillColor(248, 250, 252); // Slate 50
      const boxPadding = 5;
      const boxH = (justificationLines.length * 5) + (boxPadding * 2);
      doc.roundedRect(leftColX, y, colWidth, boxH, 2, 2, 'F');
      doc.setFont("helvetica", "normal");
      doc.setTextColor(15, 23, 42);
      doc.text(justificationLines, leftColX + boxPadding, y + boxPadding + 3);
      y += boxH + 10;

      // Analysis
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(71, 85, 105);
      doc.text('ANALYSIS & INSIGHT', leftColX, y);
      y += 6;
      doc.setFont("helvetica", "italic");
      doc.setTextColor(15, 23, 42);
      doc.setFontSize(10);
      doc.text(analysisLines, leftColX, y);
      y += (analysisLines.length * 5);

      if (analysisSubLines && analysisSubLines.length > 0) {
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(71, 85, 105);
        y += 2;
        doc.text(analysisSubLines, leftColX, y);
        y += (analysisSubLines.length * 5);
      }

      const leftColBottomY = y;

      // --- RIGHT COLUMN ---
      let ry = columnsTopY;
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(71, 85, 105);
      doc.text('CONVERSATION EXCERPT', rightColX, ry);
      ry += 8;

      if (cat.excerpts && cat.excerpts.length > 0) {
        cat.excerpts.forEach((ex: any) => {
          if (ex.label === 'Recommended approach') {
            ry += 5;
            doc.setFillColor(240, 253, 244); // Green 50
            const plainText = ex.text.replace(/\*\*/g, '');
            doc.setFont("helvetica", "bold");
            const recLines = doc.splitTextToSize(plainText, colWidth - 10);
            const recBoxH = (recLines.length * 5) + 12;
            doc.roundedRect(rightColX, ry, colWidth, recBoxH, 2, 2, 'F');
            doc.setTextColor(21, 128, 61); // Green 700
            doc.setFont("helvetica", "bold");
            doc.text('Recommended approach', rightColX + 5, ry + 6);
            doc.setFontSize(9);
            this.renderMarkdownPDF(doc, ex.text, rightColX + 5, ry + 12, colWidth - 10);
            ry += recBoxH + 5;
          } else {
            doc.setFontSize(8);
            doc.setTextColor(148, 163, 184); // Slate 400
            doc.setFont("helvetica", "bold");
            doc.text(ex.label.toUpperCase(), rightColX, ry);
            ry += 5;

            doc.setFontSize(9);
            doc.setFont("helvetica", "normal");

            // Speaker Logic
            if (ex.role === 'AGENT') {
              doc.setTextColor(79, 70, 229); // Indigo 600
            } else if (ex.role === 'CUSTOMER') {
              doc.setTextColor(37, 99, 235); // Blue 600
            } else if (ex.role === 'AI') {
              doc.setTextColor(147, 51, 234); // Purple 600
            } else {
              doc.setTextColor(71, 85, 105); // Slate 600
            }

            const excerptLines = doc.splitTextToSize(ex.text, colWidth);
            doc.text(excerptLines, rightColX, ry);
            ry += (excerptLines.length * 5) + 6;
          }
        });
      }

      y = Math.max(leftColBottomY, ry) + 10;

      // --- SECOND ROW: WHAT WENT WELL & OPPORTUNITIES ---
      const listStartY = y;

      // What Went Well (Left)
      if (cat.wentWell && cat.wentWell.length > 0) {
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(34, 197, 94); // Green 500
        doc.text('WHAT WENT WELL', leftColX, y);
        y += 6;
        doc.setFont("helvetica", "normal");
        doc.setTextColor(71, 85, 105);
        cat.wentWell.forEach((item: string) => {
          const lines = doc.splitTextToSize(`• ${item}`, colWidth);
          doc.text(lines, leftColX, y);
          y += (lines.length * 5) + 2;
        });
      }
      const wentWellBottomY = y;

      // Opportunities (Right)
      let oy = listStartY;
      if (cat.opportunities && cat.opportunities.length > 0) {
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(245, 158, 11); // Amber 500
        doc.text('OPPORTUNITIES', rightColX, oy);
        oy += 6;
        doc.setFont("helvetica", "normal");
        doc.setTextColor(71, 85, 105);
        cat.opportunities.forEach((item: string) => {
          const lines = doc.splitTextToSize(`• ${item}`, colWidth);
          doc.text(lines, rightColX, oy);
          oy += (lines.length * 5) + 2;
        });
      }

      y = Math.max(wentWellBottomY, oy) + 15;

      // Separator line
      if (index < this.auditCategories().length - 1) {
        doc.setDrawColor(241, 245, 249);
        doc.line(margin, y - 5, 190, y - 5);
      }
    });

    doc.save(`${this.getLeadDisplayName(lead).replace(/\s+/g, '_')}_Call_Report.pdf`);
  }

  private renderMarkdownPDF(doc: any, text: string, startX: number, startY: number, maxWidth: number): number {
    let currentX = startX;
    let currentY = startY;
    const lineHeight = 5;

    const paragraphs = text.split(/\r?\n/);

    paragraphs.forEach(p => {
      const tokens = p.split('**');
      tokens.forEach((token, index) => {
        const isBold = index % 2 !== 0;
        doc.setFont("helvetica", isBold ? "bold" : "normal");

        const words = token.split(/(\s+)/); // Keep whitespace
        words.forEach(word => {
          if (!word) return;
          const wordWidth = doc.getTextWidth(word);
          if (word.trim() === '') {
            if (currentX > startX) { currentX += wordWidth; }
            return;
          }
          if (currentX + wordWidth > startX + maxWidth && currentX > startX) {
            currentX = startX;
            currentY += lineHeight;
          }
          doc.text(word, currentX, currentY);
          currentX += wordWidth;
        });
      });
      currentX = startX;
      currentY += lineHeight;
    });

    doc.setFont("helvetica", "normal");
    return currentY;
  }
}
